// Match-evidence vocabulary — the ONE place the browser words "how strong is this
// match", and deliberately the other half of lib/matchGroups.ts:
//
//     matchGroups   -> the SHAPE of the match   ("1:1", "3:1 (voucher)")
//     matchEvidence -> the KEY it was tied on   ("Reference", "Policy", "Customer name")
//
// The LABELS themselves are never built here: the server ships a ready-made
// `match_evidence` cell (reporting/match_evidence.py), so nothing in the browser
// re-derives how a row matched from the frames. What lives here is only the
// label -> evidence-strength mapping the CSS colours on, plus the label -> plain
// sentence the legend reads.
//
// KIND_BY_LABEL is a direct port of reporting/match_evidence.py::VOCABULARY, and
// tests/test_match_evidence.py reads BOTH files and asserts they still agree — so a
// label added on the server can never silently render as an uncoloured chip here.

export const MATCH_EVIDENCE_COL = "match_evidence";

/** Evidence strength. Must equal reporting/match_evidence.py's KIND_* values. */
export type EvidenceKind = "identity" | "review" | "carried" | "user";

/** Port of reporting/match_evidence.py::VOCABULARY — keep the two in step. */
export const KIND_BY_LABEL: Record<string, EvidenceKind> = {
  "Reference": "identity",
  "Policy": "identity",
  "Endorsement": "identity",
  "Invoice": "identity",
  "Sales order": "identity",
  "Invoice batch": "identity",
  "Endorsement batch": "identity",
  "Invoice + endorsement": "identity",
  "Voucher + document": "identity",
  "Transaction": "identity",
  "Settlement": "identity",
  "Receipt amount": "identity",
  "Cheque": "identity",
  "Contra": "identity",
  "Customer name": "review",
  "Customer name (weak)": "review",
  "Narrated ID": "review",
  "Premium link": "review",
  "Prior period": "carried",
  "Settled earlier": "carried",
  "Manual": "user",
  "Matched": "identity",
};

/** Legend glyph per kind. Must equal reporting/match_evidence.py::_SYMBOL. */
export const SYMBOL_BY_KIND: Record<EvidenceKind, string> = {
  identity: "●",   // ● filled circle
  review: "▲",     // ▲ filled triangle
  carried: "◆",    // ◆ filled diamond
  user: "■",       // ■ filled square
};

/** The evidence kind for a cell value, or null when the cell names no match. */
export function kindOf(value: unknown): EvidenceKind | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (!s) return null;
  // An unknown label is still a real match — colour it as the weakest reading rather
  // than dropping the chip, which would leave a matched row looking unmatched.
  return KIND_BY_LABEL[s] ?? "review";
}

/**
 * Extra cell class for a grid rendering the Matched By column: a chip tinted by
 * evidence strength. Shared by every grid so the column looks the same in the
 * statement tabs, the drill-downs and Side by Side.
 *
 * `colKey` may be a composite paired-frame key ("cri::match_evidence").
 */
export function matchEvidenceCellClass(colKey: string, value: unknown): string {
  if (!colKey.endsWith(MATCH_EVIDENCE_COL)) return "";
  const k = kindOf(value);
  return k ? `mevi-cell mevi-${k}` : "";
}

/** One legend row, as the API ships it (api/serialize.py::match_evidence_legend). */
export interface EvidenceLegendEntry {
  label: string;
  symbol: string;
  kind: EvidenceKind;
  sentence: string;
  rows: number;
}

/**
 * The legend for what is actually on screen: the server's vocabulary (which owns the
 * wording and the order) narrowed to the labels `counts` saw, carrying those counts.
 *
 * Counting is the caller's so the legend foots to the rows the reader can see rather
 * than to a whole-reconciliation total — a legend claiming 41 bridge matches above a
 * grid filtered to 3 of them is worse than no legend.
 */
export function legendFor(
  vocabulary: EvidenceLegendEntry[],
  counts: Map<string, number>,
): EvidenceLegendEntry[] {
  const seen = new Set<string>();
  const out: EvidenceLegendEntry[] = [];
  for (const e of vocabulary) {
    const n = counts.get(e.label) ?? 0;
    seen.add(e.label);
    if (n > 0) out.push({ ...e, rows: n });
  }
  // A label the server's vocabulary does not list still gets a line: it is on screen,
  // so leaving it out of the legend would leave those rows unexplained.
  for (const [label, n] of counts) {
    if (!seen.has(label) && n > 0) {
      const kind = kindOf(label) ?? "review";
      out.push({ label, symbol: SYMBOL_BY_KIND[kind], kind, rows: n, sentence: "" });
    }
  }
  return out;
}

/** Count the Matched By values over `rows` at column index `idx` (-1 => empty). */
export function countEvidence(
  rows: readonly (readonly (string | number | boolean | null)[])[],
  idx: number,
): Map<string, number> {
  const counts = new Map<string, number>();
  if (idx < 0) return counts;
  for (const r of rows) {
    const v = r[idx];
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (!s) continue;
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  return counts;
}
